import React, { useState, useRef } from 'react';
import { PhotoIcon, CheckIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { usePageContents } from '../../contexts/PageContentContext';
import { uploadImageToStorage } from '../../utils/imageUpload';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { BoldIcon, ItalicIcon, ListBulletIcon, NumberedListIcon } from '@heroicons/react/24/outline';

const PAGE_INFO = {
  greeting: {
    title: '인사말',
    description: '인사말 페이지의 타이틀과 내용을 관리합니다.'
  },
  about_gtc: {
    title: 'DGTC소개',
    description: 'DGTC소개 페이지의 타이틀과 내용을 관리합니다.'
  },
  curriculum: {
    title: '교육과정',
    description: '교육과정 페이지의 타이틀과 내용을 관리합니다.'
  },
  facilities: {
    title: '시설안내',
    description: '시설안내 페이지의 타이틀과 내용을 관리합니다.'
  },
  location: {
    title: '오시는 길',
    description: '오시는 길 페이지의 타이틀과 내용을 관리합니다.'
  },
  programs: {
    title: '프로그램',
    description: '프로그램 메인 페이지의 타이틀과 내용을 관리합니다.'
  },
  notice: {
    title: '공지사항',
    description: '공지사항 메인 페이지의 타이틀과 내용을 관리합니다.'
  },
  gallery: {
    title: '갤러리',
    description: '갤러리 메인 페이지의 타이틀과 내용을 관리합니다.'
  },
  program_group: {
    title: '단체체험',
    description: '단체체험 프로그램 페이지의 타이틀과 내용을 관리합니다.'
  },
  program_preschool: {
    title: '유치과정',
    description: '유치과정 프로그램 페이지의 타이틀과 내용을 관리합니다.'
  },
  program_elementary: {
    title: '초등과정',
    description: '초등과정 프로그램 페이지의 타이틀과 내용을 관리합니다.'
  },
  program_secondary: {
    title: '중·고등과정',
    description: '중·고등과정 프로그램 페이지의 타이틀과 내용을 관리합니다.'
  },
  program_adult: {
    title: '성인과정',
    description: '성인과정 프로그램 페이지의 타이틀과 내용을 관리합니다.'
  },
  program_special: {
    title: '특별과정',
    description: '특별과정 프로그램 페이지의 타이틀과 내용을 관리합니다.'
  },
  program_camp: {
    title: '방학캠프',
    description: '방학캠프 프로그램 페이지의 타이틀과 내용을 관리합니다.'
  },
  program_international: {
    title: '국제교류',
    description: '국제교류 프로그램 페이지의 타이틀과 내용을 관리합니다.'
  },
  // 약관 관리
  terms_service: {
    title: '이용약관',
    description: '회원가입 시 표시되는 이용약관을 관리합니다.'
  },
  terms_privacy: {
    title: '개인정보 수집·이용',
    description: '회원가입 시 표시되는 개인정보 수집·이용 동의서를 관리합니다.'
  },
  terms_third_party: {
    title: '개인정보 제3자 제공',
    description: '회원가입 시 표시되는 개인정보 제3자 제공 동의서를 관리합니다.'
  }
};

// 기본값 (Supabase에 데이터가 없을 때 사용)
const DEFAULT_VALUES: Record<string, { title_main: string; title_sub: string; title_eng: string; description: string }> = {
  greeting: {
    title_main: '동두천시 글로벌인재교육센터를',
    title_sub: '방문해주신 여러분을',
    title_eng: '진심으로 환영합니다.',
    description: '<p>동두천시 글로벌인재교육센터는 <strong>동두천시가 설립하고 ㈜한원글로벌이 위탁 운영하는</strong> 지역 대표 글로벌 교육기관으로, 미래세대가 세계와 소통하며 성장할 수 있도록 창의적이고 실용적인 글로벌 교육 프로그램을 운영하고 있습니다.</p>'
  },
  about_gtc: {
    title_main: '국제교육을 통한',
    title_sub: '글로벌 창의인재 양성',
    title_eng: 'Fostering Creative Global Leaders through International Education',
    description: '<p>동두천시 글로벌인재교육센터는 <strong style="color: #111827">글로벌 시대에 필요한 언어 능력, 문화 감수성, 창의적 사고</strong>를 함께 키우는 국제교육 전문기관입니다.</p><p>유아부터 성인까지 모든 세대가 함께 배우며 성장할 수 있는 열린 배움의 공간으로, 언어교육을 넘어 세계 시민으로 성장하기 위한 포괄적 글로벌 교육 프로그램을 운영하고 있습니다.</p><p>센터는 국제 이해와 상호문화 소통을 기반으로 <strong style="color: #059669">언어·과학·예술·기술이 융합된 체험형 교육</strong>을 통해 학습자가 스스로 배우고, 생각하고, 세상과 연결되는 능력을 기를 수 있도록 돕습니다.</p>'
  },
  facilities: {
    title_main: '창의와 체험이 공존하는',
    title_sub: '글로벌 교육공간',
    title_eng: 'A Creative and Experiential Space for Global Learning',
    description: '<p>동두천시 글로벌인재교육센터는 <strong style="color: #111827">언어, 문화, 과학, 예술이 조화를 이루는 복합형 글로벌 교육공간</strong>으로, 수강생이 다양한 체험을 통해 스스로 배우고 탐구할 수 있는 창의적 학습 환경을 제공합니다.</p><p>유아부터 성인까지 모든 세대가 편안하고 안전하게 학습할 수 있도록 <strong style="color: #059669">연령과 프로그램 특성에 맞춘 전용 교육실과 체험존</strong>을 갖추었으며, 최신 ICT 기술을 기반으로 한 디지털 학습 시스템을 구축하여 효율적이고 몰입도 높은 교육이 이루어지도록 하였습니다.</p>'
  },
  curriculum: {
    title_main: '글로벌 시대를 준비하는',
    title_sub: '체계적인 교육과정',
    title_eng: 'Systematic Curriculum for the Global Era',
    description: '<p>동두천시 글로벌인재교육센터는 <strong style="color: #111827">유아부터 성인까지</strong> 모든 세대를 위한 체계적인 글로벌 교육 프로그램을 운영합니다.</p>'
  },
  location: {
    title_main: '동두천시 글로벌인재교육센터',
    title_sub: '오시는 길',
    title_eng: 'Directions to DGTC',
    description: '<p><strong>주소:</strong> 경기도 동두천시</p><p><strong>전화:</strong> 031-863-3522</p>'
  },
  programs: {
    title_main: '다양한 프로그램으로',
    title_sub: '글로벌 역량을 키워보세요',
    title_eng: 'Develop Global Competencies',
    description: '<p>동두천시 글로벌인재교육센터는 <strong style="color: #059669">유아부터 성인까지</strong> 다양한 글로벌 교육 프로그램을 제공합니다.</p>'
  },
  notice: {
    title_main: '동두천시 글로벌인재교육센터',
    title_sub: '공지사항',
    title_eng: 'Notice',
    description: '<p>센터의 새로운 소식과 공지사항을 확인하세요.</p>'
  },
  gallery: {
    title_main: '동두천시 글로벌인재교육센터',
    title_sub: '갤러리',
    title_eng: 'Gallery',
    description: '<p>센터의 다양한 활동과 프로그램 현장을 사진으로 만나보세요.</p>'
  },
  program_group: {
    title_main: '글로벌 체험 프로그램',
    title_sub: '단체체험',
    title_eng: 'Global Experience Program',
    description: '<p>영어·과학·문화가 융합된 1일형 체험학습 프로그램으로, 원어민 교사와 함께 영어·STEAM·AI 체험활동을 즐기며 창의력과 협동심, 글로벌 감각을 키웁니다.</p>'
  },
  program_preschool: {
    title_main: '유아 영어과정',
    title_sub: '유치과정',
    title_eng: 'Early Learners English Program',
    description: '<p>스토리텔링, 노래, 놀이 중심의 오감형 영어체험 과정으로, 파닉스와 생활회화를 자연스럽게 익히며 언어감각·표현력·사회성을 함께 기릅니다.</p>'
  },
  program_elementary: {
    title_main: '초등 영어·창의과정',
    title_sub: '초등과정',
    title_eng: 'Elementary English & STEAM Program',
    description: '<p>기초 영어회화, 파닉스, 독서·토론·쓰기 훈련을 중심으로 언어의 기초를 확립하고, STEAM·문화체험을 통해 탐구력과 창의력을 함께 키웁니다.</p>'
  },
  program_secondary: {
    title_main: '중·고등 글로벌 리더십과정',
    title_sub: '중·고등과정',
    title_eng: 'Secondary Global Leadership Program',
    description: '<p>실전 영어 활용능력과 글로벌 리더십을 키우는 과정으로, 토론·스피치·에세이·ELTiS 대비를 통해 국제학교 진학 및 해외 교류를 준비합니다.</p>'
  },
  program_adult: {
    title_main: '성인 글로벌 커뮤니케이션',
    title_sub: '성인과정',
    title_eng: 'Adult Global Communication & Life Skill Program',
    description: '<p>실생활·비즈니스·여행을 포함한 다양한 목적에 맞는 실용 영어회화부터 쿠킹 클래스, 글로벌 문화 체험, 테마 워크숍 등 국제 감각을 균형 있게 향상시키는 통합형 프로그램입니다.</p>'
  },
  program_special: {
    title_main: '글로벌 소통·교육 커뮤니티',
    title_sub: '특별과정',
    title_eng: 'Global Communication & Community Program',
    description: '<p>글로벌 의사소통 강화 및 교육 협력을 위한 특별 프로그램입니다.</p>'
  },
  program_camp: {
    title_main: '글로벌 교류 캠프',
    title_sub: '방학캠프',
    title_eng: 'Global Exchange Camp',
    description: '<p>국내 학생과 외국 학생이 함께하는 Korean International Camp로, 한국어·영어 공동 프로젝트, 문화교류, 봉사활동 등을 통해 진정한 국제 우정을 형성하는 체험형 글로벌 캠프입니다.</p>'
  },
  program_international: {
    title_main: '국제교류 프로그램',
    title_sub: '국제교류',
    title_eng: 'International Exchange Program',
    description: '<p>해외 명문학교와의 교류, 단기 국제스쿨링 캠프, 외국인 장학생 초청 등 다양한 형태의 국제 교류 프로그램으로, 실질적인 언어·문화 교류를 통해 글로벌 역량을 함께 키워나갑니다.</p>'
  },
  // 약관 기본값
  terms_service: {
    title_main: '이용약관',
    title_sub: '',
    title_eng: '',
    description: ''
  },
  terms_privacy: {
    title_main: '개인정보 수집·이용 동의',
    title_sub: '',
    title_eng: '',
    description: ''
  },
  terms_third_party: {
    title_main: '개인정보 제3자 제공 동의',
    title_sub: '',
    title_eng: '',
    description: ''
  }
};

type PageKey = 'greeting' | 'about_gtc' | 'curriculum' | 'facilities' | 'location' | 'programs' | 'notice' | 'gallery' |
               'program_group' | 'program_preschool' | 'program_elementary' | 'program_secondary' |
               'program_adult' | 'program_special' | 'program_camp' | 'program_international' |
               'terms_service' | 'terms_privacy' | 'terms_third_party';

// 유튜브 URL을 embed URL로 변환하는 함수
const getYoutubeEmbedUrl = (url: string): string => {
  if (!url) return '';

  // 이미 embed URL인 경우 그대로 반환
  if (url.includes('youtube.com/embed/')) {
    return url;
  }

  // 다양한 유튜브 URL 형식 처리
  let videoId = '';

  // youtube.com/watch?v=VIDEO_ID
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) {
    videoId = watchMatch[1];
  }

  // youtu.be/VIDEO_ID
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) {
    videoId = shortMatch[1];
  }

  // youtube.com/v/VIDEO_ID
  const vMatch = url.match(/youtube\.com\/v\/([^?&]+)/);
  if (vMatch) {
    videoId = vMatch[1];
  }

  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }

  return url; // 변환 실패 시 원본 반환
};

const PageContentManagement: React.FC = () => {
  const { pageContents, loading, updatePageContent } = usePageContents();
  const [selectedPage, setSelectedPage] = useState<PageKey>('greeting');
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const descImageInputRef = useRef<HTMLInputElement>(null);

  // 선택된 페이지 콘텐츠 가져오기
  const currentContent = pageContents.find(content => content.page_key === selectedPage);

  // media_type 추론 함수: DB에 media_type이 없으면 image_url 유무로 판단
  const inferMediaType = (content: typeof currentContent): 'image' | 'video' => {
    if (content?.media_type) return content.media_type;
    // 기존 데이터: media_type이 없으면 image_url이 있으면 image, 아니면 기본값 image
    return 'image';
  };

  // Form state - 기본값 사용
  const defaultValues = DEFAULT_VALUES[selectedPage];
  const [formData, setFormData] = useState({
    title_main: currentContent?.title_main || defaultValues.title_main,
    title_sub: currentContent?.title_sub || defaultValues.title_sub,
    title_eng: currentContent?.title_eng || defaultValues.title_eng,
    description: currentContent?.description || defaultValues.description,
    image_url: currentContent?.image_url || '',
    video_url: currentContent?.video_url || '',
    media_type: inferMediaType(currentContent),
    video_autoplay: currentContent?.video_autoplay || false
  });

  // TipTap Editor for description
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      Placeholder.configure({
        placeholder: '페이지 설명을 입력하세요...',
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
    ],
    content: formData.description,
    editorProps: {
      attributes: {
        class: 'prose max-w-none focus:outline-none',
      },
    },
    parseOptions: {
      preserveWhitespace: 'full',
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setFormData(prev => ({ ...prev, description: html }));
    },
  });

  // HTML 변환 함수: <strong style="color: ..."> → <span style="color: ..."><strong>
  const convertStyledTagsForEditor = (html: string): string => {
    // <strong style="color: #xxx">텍스트</strong> → <span style="color: #xxx"><strong>텍스트</strong></span>
    let converted = html.replace(
      /<(strong|em|b|i)\s+style="([^"]*color:\s*[^;"]+[^"]*)">([^<]+)<\/\1>/gi,
      '<span style="$2"><$1>$3</$1></span>'
    );
    return converted;
  };

  // HTML 역변환 함수: <span style="color: ..."><strong> → <strong style="color: ...">
  const convertStyledTagsForSave = (html: string): string => {
    // <span style="color: #xxx"><strong>텍스트</strong></span> → <strong style="color: #xxx">텍스트</strong>
    let converted = html.replace(
      /<span\s+style="([^"]*color:\s*[^;"]+[^"]*)"><(strong|em|b|i)>([^<]+)<\/\2><\/span>/gi,
      '<$2 style="$1">$3</$2>'
    );
    return converted;
  };

  // Update form when page changes
  React.useEffect(() => {
    const defaults = DEFAULT_VALUES[selectedPage];
    const newFormData = {
      title_main: currentContent?.title_main || defaults.title_main,
      title_sub: currentContent?.title_sub || defaults.title_sub,
      title_eng: currentContent?.title_eng || defaults.title_eng,
      description: currentContent?.description || defaults.description,
      image_url: currentContent?.image_url || '',
      video_url: currentContent?.video_url || '',
      media_type: inferMediaType(currentContent),
      video_autoplay: currentContent?.video_autoplay || false
    };

    setFormData(newFormData);

    if (editor) {
      console.log('=== EDITOR DEBUG ===');
      console.log('Original description:', newFormData.description);
      const convertedDescription = convertStyledTagsForEditor(newFormData.description);
      console.log('Converted description:', convertedDescription);
      editor.commands.setContent(convertedDescription);

      // 에디터 내부 실제 DOM 확인
      setTimeout(() => {
        console.log('Editor HTML after load:', editor.getHTML());
        const editorElement = document.querySelector('.ProseMirror');
        if (editorElement) {
          console.log('Editor DOM innerHTML:', editorElement.innerHTML);
        }
        console.log('Editor JSON:', JSON.stringify(editor.getJSON(), null, 2));
      }, 100);
      console.log('===================');
    }
  }, [selectedPage, currentContent, editor]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const imageUrl = await uploadImageToStorage(file, 'page-images');
        setFormData(prev => ({ ...prev, image_url: imageUrl }));
        console.log('Page image uploaded successfully:', imageUrl);
      } catch (error) {
        console.error('Failed to upload page image:', error);
        alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const handleImageRemove = () => {
    if (window.confirm('이미지를 삭제하시겠습니까?')) {
      setFormData(prev => ({ ...prev, image_url: '' }));
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDescriptionImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && editor) {
      try {
        const imageUrl = await uploadImageToStorage(file, 'description-images');
        // 중앙 정렬된 이미지 삽입 - div로 감싸기
        editor.chain().focus().insertContent(`<div style="text-align: center; margin: 2rem 0;"><img src="${imageUrl}" style="max-width: 100%; height: auto; display: inline-block;" /></div>`).run();
        console.log('Description image uploaded successfully:', imageUrl);
      } catch (error) {
        console.error('Failed to upload description image:', error);
        alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const addImageToDescription = () => {
    descImageInputRef.current?.click();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const editorHtml = editor?.getHTML() || formData.description;
      // 저장할 때는 역변환: <span style="color: ..."><strong> → <strong style="color: ...">
      const convertedHtml = convertStyledTagsForSave(editorHtml);
      console.log('Saving - Editor HTML:', editorHtml);
      console.log('Saving - Converted HTML:', convertedHtml);
      await updatePageContent(selectedPage, {
        ...formData,
        description: convertedHtml
      });
      alert('페이지 콘텐츠가 저장되었습니다!');
    } catch (error) {
      console.error('Failed to save page content:', error);
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">페이지 콘텐츠 관리</h1>
        <p className="text-gray-600">모든 주요 페이지의 배너, 타이틀, 설명, 이미지를 관리합니다.</p>
      </div>

      {/* Page Selector - Grouped */}
      <div className="mb-8 space-y-6">
        {/* 센터소개 그룹 */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
            <h3 className="text-lg font-bold text-gray-800">센터소개</h3>
            <span className="text-xs text-gray-500 ml-2">(5개 페이지)</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(['greeting', 'about_gtc', 'curriculum', 'facilities', 'location'] as PageKey[]).map((pageKey) => (
              <button
                key={pageKey}
                onClick={() => setSelectedPage(pageKey)}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap text-sm ${
                  selectedPage === pageKey
                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-700 shadow-sm hover:shadow-md border border-blue-100'
                }`}
              >
                {PAGE_INFO[pageKey].title}
              </button>
            ))}
          </div>
        </div>

        {/* 프로그램 그룹 */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-6 bg-green-600 rounded-full"></div>
            <h3 className="text-lg font-bold text-gray-800">프로그램</h3>
            <span className="text-xs text-gray-500 ml-2">(9개 페이지)</span>
          </div>

          {/* 메인 프로그램 페이지 */}
          <div className="mb-3">
            <button
              onClick={() => setSelectedPage('programs')}
              className={`px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap text-sm ${
                selectedPage === 'programs'
                  ? 'bg-green-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-green-50 hover:text-green-700 shadow-sm hover:shadow-md border border-green-100'
              }`}
            >
              📋 {PAGE_INFO['programs'].title} (메인)
            </button>
          </div>

          {/* 프로그램 하위 카테고리 */}
          <div className="pl-6 border-l-2 border-green-200">
            <p className="text-xs text-gray-600 mb-2 font-medium">프로그램 카테고리</p>
            <div className="flex gap-2 flex-wrap">
              {(['program_group', 'program_preschool', 'program_elementary', 'program_secondary',
                 'program_adult', 'program_special', 'program_camp', 'program_international'] as PageKey[]).map((pageKey) => (
                <button
                  key={pageKey}
                  onClick={() => setSelectedPage(pageKey)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap text-sm ${
                    selectedPage === pageKey
                      ? 'bg-green-500 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:bg-green-50 hover:text-green-600 shadow-sm hover:shadow border border-green-100'
                  }`}
                >
                  {PAGE_INFO[pageKey].title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 기타 페이지 */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-6 bg-purple-600 rounded-full"></div>
            <h3 className="text-lg font-bold text-gray-800">기타</h3>
            <span className="text-xs text-gray-500 ml-2">(2개 페이지)</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(['notice', 'gallery'] as PageKey[]).map((pageKey) => (
              <button
                key={pageKey}
                onClick={() => setSelectedPage(pageKey)}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap text-sm ${
                  selectedPage === pageKey
                    ? 'bg-purple-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-purple-50 hover:text-purple-700 shadow-sm hover:shadow-md border border-purple-100'
                }`}
              >
                {PAGE_INFO[pageKey].title}
              </button>
            ))}
          </div>
        </div>

        {/* 약관 관리 */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-6 bg-orange-600 rounded-full"></div>
            <h3 className="text-lg font-bold text-gray-800">약관 관리</h3>
            <span className="text-xs text-gray-500 ml-2">(3개 약관)</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(['terms_service', 'terms_privacy', 'terms_third_party'] as PageKey[]).map((pageKey) => (
              <button
                key={pageKey}
                onClick={() => setSelectedPage(pageKey)}
                className={`px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 whitespace-nowrap text-sm ${
                  selectedPage === pageKey
                    ? 'bg-orange-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-orange-50 hover:text-orange-700 shadow-sm hover:shadow-md border border-orange-100'
                }`}
              >
                {PAGE_INFO[pageKey].title}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Form */}
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {PAGE_INFO[selectedPage].title}
          </h2>
          <p className="text-sm text-gray-600">{PAGE_INFO[selectedPage].description}</p>
        </div>

        <div className="space-y-6">
          {/* 타이틀 필드들 */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                메인 타이틀
              </label>
              <input
                type="text"
                name="title_main"
                value={formData.title_main}
                onChange={handleInputChange}
                placeholder="예: 동두천시 글로벌인재교육센터를"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                서브 타이틀
              </label>
              <input
                type="text"
                name="title_sub"
                value={formData.title_sub}
                onChange={handleInputChange}
                placeholder="예: 방문해주신 여러분을"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                영문 타이틀
              </label>
              <input
                type="text"
                name="title_eng"
                value={formData.title_eng}
                onChange={handleInputChange}
                placeholder="예: Welcome to DGTC"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* 미디어 타입 선택 (이미지 또는 영상) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              페이지 미디어
            </label>

            {/* 미디어 타입 선택 탭 */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, media_type: 'image' }))}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  formData.media_type === 'image'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <PhotoIcon className="h-5 w-5" />
                이미지
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, media_type: 'video' }))}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  formData.media_type === 'video'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <VideoCameraIcon className="h-5 w-5" />
                영상 (유튜브)
              </button>
            </div>

            {/* 이미지 업로드 영역 */}
            {formData.media_type === 'image' && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                {formData.image_url && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-gray-600">현재 이미지:</p>
                      <button
                        type="button"
                        onClick={handleImageRemove}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        이미지 삭제
                      </button>
                    </div>
                    <img
                      src={formData.image_url}
                      alt="페이지 이미지"
                      className="w-full max-w-2xl h-64 object-cover rounded-lg border border-gray-300"
                    />
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.image_url
                    ? '새 이미지를 업로드하려면 파일을 선택하세요'
                    : '페이지에 표시될 이미지를 업로드하세요'}
                </p>
              </div>
            )}

            {/* 영상 URL 입력 영역 */}
            {formData.media_type === 'video' && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <input
                  type="text"
                  name="video_url"
                  value={formData.video_url}
                  onChange={handleInputChange}
                  placeholder="유튜브 영상 URL을 입력하세요 (예: https://www.youtube.com/watch?v=...)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  유튜브 영상 URL을 입력하면 페이지에 영상이 표시됩니다.
                </p>

                {/* 자동재생 옵션 */}
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.video_autoplay}
                    onChange={(e) => setFormData(prev => ({ ...prev, video_autoplay: e.target.checked }))}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">자동재생 (음소거 상태로 재생)</span>
                </label>

                {/* 영상 미리보기 */}
                {formData.video_url && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-2">영상 미리보기:</p>
                    <div className="relative w-full max-w-2xl aspect-video rounded-lg overflow-hidden border border-gray-300">
                      <iframe
                        src={getYoutubeEmbedUrl(formData.video_url)}
                        title="영상 미리보기"
                        className="absolute inset-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 설명 (HTML 에디터) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              페이지 설명
            </label>
            <div className="border rounded-lg border-gray-300 bg-white">
              {/* 에디터 툴바 */}
              <div className="flex items-center gap-0.5 p-2 border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                  className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                    editor?.isActive('bold') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                  }`}
                  title="Bold"
                >
                  <BoldIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                  className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                    editor?.isActive('italic') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                  }`}
                  title="Italic"
                >
                  <ItalicIcon className="h-5 w-5" />
                </button>

                <div className="w-px h-6 bg-gray-300 mx-1"></div>

                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`px-2.5 py-1.5 rounded hover:bg-gray-100 transition-colors font-semibold text-sm ${
                    editor?.isActive('heading', { level: 2 }) ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                  }`}
                  title="Heading 2"
                >
                  H2
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`px-2.5 py-1.5 rounded hover:bg-gray-100 transition-colors font-semibold text-sm ${
                    editor?.isActive('heading', { level: 3 }) ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                  }`}
                  title="Heading 3"
                >
                  H3
                </button>

                <div className="w-px h-6 bg-gray-300 mx-1"></div>

                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}
                  className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                    editor?.isActive('bulletList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                  }`}
                  title="Bullet List"
                >
                  <ListBulletIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                  className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                    editor?.isActive('orderedList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                  }`}
                  title="Numbered List"
                >
                  <NumberedListIcon className="h-5 w-5" />
                </button>

                <div className="w-px h-6 bg-gray-300 mx-1"></div>

                {/* 색상 버튼들 */}
                <button
                  type="button"
                  onClick={() => {
                    if (!editor) return;
                    const { from, to } = editor.state.selection;
                    const text = editor.state.doc.textBetween(from, to, '');
                    if (text) {
                      editor.chain().focus().deleteSelection().insertContent(`<span style="color: #000000">${text}</span>`).run();
                    }
                  }}
                  className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-500"
                  style={{ backgroundColor: '#000000' }}
                  title="검정색"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!editor) return;
                    const { from, to } = editor.state.selection;
                    const text = editor.state.doc.textBetween(from, to, '');
                    if (text) {
                      editor.chain().focus().deleteSelection().insertContent(`<span style="color: #6B7280">${text}</span>`).run();
                    }
                  }}
                  className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-500"
                  style={{ backgroundColor: '#6B7280' }}
                  title="회색"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!editor) return;
                    const { from, to } = editor.state.selection;
                    const text = editor.state.doc.textBetween(from, to, '');
                    if (text) {
                      editor.chain().focus().deleteSelection().insertContent(`<span style="color: #10B981">${text}</span>`).run();
                    }
                  }}
                  className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-500"
                  style={{ backgroundColor: '#10B981' }}
                  title="초록색 (green-500)"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!editor) return;
                    const { from, to } = editor.state.selection;
                    const text = editor.state.doc.textBetween(from, to, '');
                    if (text) {
                      editor.chain().focus().deleteSelection().insertContent(`<span style="color: #059669">${text}</span>`).run();
                    }
                  }}
                  className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-500"
                  style={{ backgroundColor: '#059669' }}
                  title="진한 초록색 (green-600)"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!editor) return;
                    const { from, to } = editor.state.selection;
                    const text = editor.state.doc.textBetween(from, to, '');
                    if (text) {
                      editor.chain().focus().deleteSelection().insertContent(`<span style="color: #14B8A6">${text}</span>`).run();
                    }
                  }}
                  className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-500"
                  style={{ backgroundColor: '#14B8A6' }}
                  title="청록색 (teal-500)"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!editor) return;
                    const { from, to } = editor.state.selection;
                    const text = editor.state.doc.textBetween(from, to, '');
                    if (text) {
                      editor.chain().focus().deleteSelection().insertContent(`<span style="color: #3B82F6">${text}</span>`).run();
                    }
                  }}
                  className="w-6 h-6 rounded border-2 border-gray-300 hover:border-gray-500"
                  style={{ backgroundColor: '#3B82F6' }}
                  title="파란색 (blue-500)"
                />

                <div className="w-px h-6 bg-gray-300 mx-1"></div>

                <button
                  type="button"
                  onClick={addImageToDescription}
                  className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
                  title="Insert Image"
                >
                  <PhotoIcon className="h-5 w-5" />
                </button>
                <input
                  ref={descImageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleDescriptionImageUpload}
                  className="hidden"
                />

              </div>
              {/* 에디터 본문 */}
              <EditorContent
                editor={editor}
                className="prose max-w-none p-4 min-h-[200px] focus:outline-none"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              페이지에 표시될 설명 텍스트입니다. 볼드, 이탤릭, 리스트 등의 서식을 사용할 수 있습니다.
            </p>
          </div>

          {/* 저장 버튼 */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  저장 중...
                </>
              ) : (
                <>
                  <CheckIcon className="h-5 w-5 mr-2" />
                  저장
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageContentManagement;
